/**
 * Font downloader — tipos puros, constantes y utilidades agnósticas de plataforma.
 *
 * Este módulo deliberadamente NO contiene lógica de descarga ni acceso a disco.
 * Cada plataforma implementa `IFontDownloaderService` de forma independiente:
 *
 * @platform-implementation
 *   Native (Expo):  apps/native/utils/font-downloader.ts
 *     → usa expo-font (Font.loadAsync) + expo-file-system (File, Directory)
 *     → caché en disco: FileSystem.Paths.document/fonts/<family>-<weight>.ttf
 *     → descarga desde API CDN o URL de variante como fallback
 *
 *   Web (SolidJS):  apps/web/src/utils/font-downloader.ts
 *     → uses FontFace API: `new FontFace(key, \`url("${url}")\`).load()`
 *     → cache via browser memory (loadedFontKeys) and register to document.fonts.
 *     → supports clearing family keys from the browser's font registry.
 */

import type { AbstractPowerSyncDatabase } from "@powersync/common";

// ── Tipos ─────────────────────────────────────────────────────────────────────

export const INGESTABLE_WEIGHTS = [
  "100",
  "200",
  "300",
  "400",
  "500",
  "600",
  "700",
  "800",
  "900",
] as const;
export type IngestableWeight = (typeof INGESTABLE_WEIGHTS)[number];

export type FontWeight = IngestableWeight;

export type FontCategory = "sans-serif" | "serif" | "display" | "handwriting" | "monospace";

export interface FontDownloaderVariant {
  weight: FontWeight;
  /** URL de descarga. Puede estar ausente si se resuelve vía attachment_id. */
  url?: string;
  /** ID del attachment en PowerSync para resolver la fuente desde almacenamiento local. */
  attachment_id?: string;
  id?: string;
  font_family_id?: string | null;
}

export interface CatalogFont {
  id: string;
  family: string;
  name: string;
  category: FontCategory;
}

/**
 * Contrato que cada implementación platform-específica debe cumplir.
 *
 * @platform-implementation
 *   - Native: apps/native/utils/font-downloader.ts → FontDownloaderService
 *   - Web:    TODO apps/web/utils/font-downloader.ts → FontDownloaderService
 */
export interface IFontDownloaderService {
  /** Retorna true si la clave de fuente ya fue cargada en memoria. */
  isLoaded(fontKey: string): boolean;
  /** Elimina del registro en memoria (y del motor de fuentes en web) todas las variantes de una familia. */
  clearFamilyKeys(family: string): void;
  /**
   * Estado de caché en disco (native) / document.fonts (web).
   * Retorna "not-applicable" en plataformas donde no aplica (ej. web sin implementación).
   */
  getDiskCacheStatus(
    family: string,
    variants: FontDownloaderVariant[],
  ): Promise<"cached" | "partial" | "not-cached" | "not-applicable">;
  /** Descarga y registra las variantes necesarias. */
  downloadAndLoad(
    family: string,
    variants: FontDownloaderVariant[],
    db?: AbstractPowerSyncDatabase,
  ): Promise<void>;
}

// ── Constantes ────────────────────────────────────────────────────────────────

export const FONT_CATEGORIES: readonly FontCategory[] = [
  "sans-serif",
  "serif",
  "display",
  "handwriting",
  "monospace",
] as const;

const FONT_WEIGHTS: ReadonlySet<string> = new Set([
  "100",
  "200",
  "300",
  "400",
  "500",
  "600",
  "700",
  "800",
  "900",
]);

const WEIGHT_NAMES: Record<FontWeight, string> = {
  "100": "Thin",
  "200": "ExtraLight",
  "300": "Light",
  "400": "Regular",
  "500": "Medium",
  "600": "SemiBold",
  "700": "Bold",
  "800": "ExtraBold",
  "900": "Black",
};

// ── Utilidades puras ──────────────────────────────────────────────────────────

export function isFontWeight(value: string | null | undefined): value is FontWeight {
  return typeof value === "string" && FONT_WEIGHTS.has(value);
}

/**
 * Genera la clave única de fuente para un par familia+peso.
 * Ejemplo: getFontKey("Open Sans", "400") → "OpenSans-Regular"
 */
export function getFontKey(family: string, weight: FontWeight): string {
  const safeFamily = family.replaceAll(/\s+/g, "");
  return `${safeFamily}-${WEIGHT_NAMES[weight]}`;
}
